import { useMemo, type ReactNode } from 'react'
import { Search, GripVertical } from 'lucide-react'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { SortHeader } from './SortHeader'
import { HeaderFilterButton } from './HeaderFilterButton'
import { FilterChips } from './FilterChips'
import { ColumnSettingsPopover } from './ColumnSettingsPopover'
import { applyColumnPrefs, useColumnPrefs } from './preferences'
import { usePermissionFilteredColumns } from './usePermissionFilteredColumns'
import type { FilterValue, TableState } from './state'
import type { ColumnDef, PaginationState, SortState } from './types'

/** Tailwind classes applied to <th>/<td> based on column.align. Default left. */
function alignClasses(align: 'left' | 'center' | 'right' | undefined): string {
  if (align === 'center') return 'text-center'
  if (align === 'right') return 'text-right'
  return 'text-left'
}

/** Inner flex alignment for header content (sort label + filter funnel). */
function headerInnerAlign(align: 'left' | 'center' | 'right' | undefined): string {
  if (align === 'center') return 'justify-center'
  if (align === 'right') return 'justify-end'
  return 'justify-start'
}

export interface DragMode<TRow> {
  /** Called with the new ordering of row keys after a drop. */
  onReorder: (orderedKeys: Array<string | number>, orderedRows: TRow[]) => void
}

export interface DataTableProps<TRow> {
  /** Stable id used as localStorage key prefix for column prefs. */
  tableId: string
  data: TRow[]
  columns: ColumnDef<TRow>[]
  rowKey: (row: TRow) => string | number
  isLoading?: boolean
  isError?: boolean
  emptyState?: ReactNode
  errorState?: ReactNode
  /** Active sort criteria, in priority order. First entry has highest priority. */
  sort?: SortState
  onSortChange?: (sort: SortState) => void
  pagination?: PaginationState
  onPaginationChange?: (next: { offset: number; limit: number }) => void
  onRowClick?: (row: TRow) => void
  /**
   * Optional table state (filters + search). Pass the {@link TableState} from
   * {@link useTableState} together with its setters; the table will render
   * filter funnels in headers and the chip bar above.
   */
  state?: TableState
  onFilterChange?: (key: string, value: FilterValue) => void
  onFilterRemove?: (key: string) => void
  onSearchChange?: (search: string) => void
  onClearAll?: () => void
  /** When true and onSearchChange is provided, a global search input appears in the toolbar. */
  searchable?: boolean
  searchPlaceholder?: string
  /** Optional toolbar slot (custom buttons). Search input is added by the engine when searchable. */
  toolbar?: ReactNode
  /**
   * Enables drag-and-drop reordering of rows. When set, the engine renders a
   * grip-handle column at the start of each row and wraps tbody in a sortable
   * context. The caller is responsible for persisting the new order (e.g.
   * a bulk PUT to a layout endpoint).
   *
   * Note: the engine assumes {@code data} is already in the desired display
   * order - do not pre-sort with another mechanism while DnD is active.
   */
  dragMode?: DragMode<TRow>
}

export function DataTable<TRow>({
  tableId,
  data,
  columns,
  rowKey,
  isLoading,
  isError,
  emptyState,
  errorState,
  sort,
  onSortChange,
  pagination,
  onPaginationChange,
  onRowClick,
  state,
  onFilterChange,
  onFilterRemove,
  onSearchChange,
  onClearAll,
  searchable,
  searchPlaceholder,
  toolbar,
  dragMode,
}: DataTableProps<TRow>) {
  const permFiltered = usePermissionFilteredColumns(columns)
  const { prefs } = useColumnPrefs(tableId)

  const { visible } = useMemo(() => applyColumnPrefs(permFiltered, prefs), [permFiltered, prefs])

  const sortIndex = useMemo(() => {
    const m = new Map<string, { direction: 'asc' | 'desc'; priority: number }>()
    sort?.forEach((c, i) => m.set(c.key, { direction: c.direction, priority: i + 1 }))
    return m
  }, [sort])

  const handleSort = (col: ColumnDef<TRow>, evt: { shiftKey: boolean }) => {
    if (!col.sortable || !onSortChange) return
    const sortKey = col.sortKey ?? col.key
    const current = sort ?? []
    const existing = current.find((c) => c.key === sortKey)

    if (!evt.shiftKey) {
      if (existing) {
        onSortChange([{ key: sortKey, direction: existing.direction === 'asc' ? 'desc' : 'asc' }])
      } else {
        onSortChange([{ key: sortKey, direction: 'asc' }])
      }
      return
    }

    if (!existing) {
      onSortChange([...current, { key: sortKey, direction: 'asc' }])
    } else if (existing.direction === 'asc') {
      onSortChange(current.map((c) => (c.key === sortKey ? { ...c, direction: 'desc' } : c)))
    } else {
      onSortChange(current.filter((c) => c.key !== sortKey))
    }
  }

  const showSearch = searchable && !!onSearchChange
  const showChips = state && (onFilterRemove || onSearchChange) && onClearAll

  /** Global continuous row numbering: page offset + position within the current page. */
  const rowOffset = pagination?.offset ?? 0
  /** Meta column sticks to the left edge, except when the DnD grip already occupies it. */
  const metaSticky = !dragMode

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  const handleDragEnd = (event: DragEndEvent) => {
    if (!dragMode) return
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIdx = data.findIndex((r) => String(rowKey(r)) === String(active.id))
    const newIdx = data.findIndex((r) => String(rowKey(r)) === String(over.id))
    if (oldIdx === -1 || newIdx === -1) return
    const reordered = arrayMove(data, oldIdx, newIdx)
    dragMode.onReorder(reordered.map(rowKey), reordered)
  }

  const headerCells = (
    <tr>
      {dragMode && <th className="w-8 px-1 py-2" aria-hidden />}
      <th
        className={cn(
          'w-10 px-1 py-2 text-center align-middle',
          metaSticky && 'sticky left-0 z-10 bg-muted',
        )}
      >
        <ColumnSettingsPopover tableId={tableId} columns={columns} iconOnly />
      </th>
      {visible.map((col) => {
        const sortKey = col.sortKey ?? col.key
        const entry = sortIndex.get(sortKey)
        const active = !!entry
        const showPriority = (sort?.length ?? 0) > 1
        const filterKey = col.filterKey ?? col.key
        const filterValue = state?.filters[filterKey]
        return (
          <th
            key={col.key}
            className={cn(
              'px-3 py-2 font-medium text-muted-foreground',
              alignClasses(col.align),
              col.className,
            )}
          >
            <div className={cn('flex items-center gap-1', headerInnerAlign(col.align))}>
              {col.sortable ? (
                <SortHeader
                  label={col.header}
                  active={active}
                  direction={entry?.direction ?? 'asc'}
                  priority={showPriority ? entry?.priority : undefined}
                  onSort={(e) => handleSort(col, e)}
                />
              ) : (
                <span>{col.header}</span>
              )}
              {col.filter && onFilterChange && (
                <HeaderFilterButton
                  config={col.filter}
                  value={filterValue}
                  onChange={(v) => onFilterChange(filterKey, v)}
                  onClear={() => onFilterRemove?.(filterKey)}
                />
              )}
            </div>
          </th>
        )
      })}
    </tr>
  )

  const totalCols = visible.length + 1 + (dragMode ? 1 : 0)

  const bodyContent = isError ? (
    <tr>
      <td colSpan={totalCols} className="px-3 py-6 text-center text-muted-foreground">
        {errorState ?? 'Nie udało się załadować danych.'}
      </td>
    </tr>
  ) : isLoading ? (
    <SkeletonRows rows={Math.min(pagination?.limit ?? 5, 8)} cols={totalCols} />
  ) : data.length === 0 ? (
    <tr>
      <td colSpan={totalCols} className="px-3 py-6 text-center text-muted-foreground">
        {emptyState ?? 'Brak wyników.'}
      </td>
    </tr>
  ) : dragMode ? (
    <SortableContext items={data.map((r) => String(rowKey(r)))} strategy={verticalListSortingStrategy}>
      {data.map((row, idx) => (
        <DraggableRow
          key={rowKey(row)}
          id={String(rowKey(row))}
          row={row}
          rowNumber={rowOffset + idx + 1}
          visible={visible}
          onRowClick={onRowClick}
        />
      ))}
    </SortableContext>
  ) : (
    data.map((row, idx) => (
      <tr
        key={rowKey(row)}
        className={cn(
          'border-b last:border-0 hover:bg-muted/40 transition-colors',
          onRowClick && 'cursor-pointer',
        )}
        onClick={onRowClick ? () => onRowClick(row) : undefined}
      >
        <td
          className={cn(
            'w-10 px-1 py-2 text-center text-xs tabular-nums text-muted-foreground',
            metaSticky && 'sticky left-0 bg-card',
          )}
        >
          {rowOffset + idx + 1}
        </td>
        {visible.map((col) => (
          <td key={col.key} className={cn('px-3 py-2', alignClasses(col.align), col.className)}>
            {col.accessor(row)}
          </td>
        ))}
      </tr>
    ))
  )

  const tableMarkup = (
    <div className="rounded-md border bg-card overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b bg-muted/40">{headerCells}</thead>
        <tbody>{bodyContent}</tbody>
      </table>
    </div>
  )

  return (
    <div className="flex flex-col gap-3">
      {(toolbar || showSearch) && (
        <div className="flex items-center gap-2">
          {showSearch && (
            <div className="relative max-w-xs flex-1">
              <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                value={state?.search ?? ''}
                onChange={(e) => onSearchChange!(e.target.value)}
                placeholder={searchPlaceholder ?? 'Szukaj...'}
                className="pl-8"
              />
            </div>
          )}
          <div className="flex-1">{toolbar}</div>
        </div>
      )}

      {showChips && (
        <FilterChips
          columns={visible}
          state={state}
          onRemoveFilter={(k) => onFilterRemove?.(k)}
          onClearSearch={() => onSearchChange?.('')}
          onClearAll={() => onClearAll!()}
        />
      )}

      {dragMode ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          {tableMarkup}
        </DndContext>
      ) : (
        tableMarkup
      )}

      {pagination && onPaginationChange && (
        <Pager pagination={pagination} onChange={onPaginationChange} />
      )}
    </div>
  )
}

function DraggableRow<TRow>({
  id, row, rowNumber, visible, onRowClick,
}: {
  id: string
  row: TRow
  rowNumber: number
  visible: ColumnDef<TRow>[]
  onRowClick?: (row: TRow) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }
  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={cn(
        'border-b last:border-0 hover:bg-muted/40 transition-colors',
        onRowClick && 'cursor-pointer',
        isDragging && 'bg-sky-50',
      )}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
      {...attributes}
    >
      <td className="w-8 px-1 py-2 text-slate-400" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing hover:text-slate-700"
          aria-label="Przeciągnij"
          {...listeners}
        >
          <GripVertical size={14} />
        </button>
      </td>
      <td className="w-10 px-1 py-2 text-center text-xs tabular-nums text-muted-foreground">
        {rowNumber}
      </td>
      {visible.map((col) => (
        <td key={col.key} className={cn('px-3 py-2', col.className)}>
          {col.accessor(row)}
        </td>
      ))}
    </tr>
  )
}

function SkeletonRows({ rows, cols }: { rows: number; cols: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="border-b last:border-0">
          {Array.from({ length: cols }).map((_, j) => (
            <td key={j} className="px-3 py-2">
              <Skeleton className="h-4 w-full" />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

function Pager({
  pagination,
  onChange,
}: {
  pagination: PaginationState
  onChange: (next: { offset: number; limit: number }) => void
}) {
  const { offset, limit, total } = pagination
  const page = Math.floor(offset / limit) + 1
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const from = total === 0 ? 0 : offset + 1
  const to = Math.min(offset + limit, total)
  return (
    <div className="flex items-center justify-between text-sm text-muted-foreground">
      <span>
        {from}–{to} z {total}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onChange({ offset: Math.max(0, offset - limit), limit })}
          className="rounded border px-2 py-1 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Poprzednia
        </button>
        <span>
          {page} / {totalPages}
        </span>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onChange({ offset: offset + limit, limit })}
          className="rounded border px-2 py-1 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Następna
        </button>
      </div>
    </div>
  )
}
